
export function getUserFriendlyError(error: unknown): string {
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return 'Network connection failed. Please check your internet and try again.';
    }
    
    // Timeout errors
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    
    // Authentication errors
    if (error.message.includes('auth') || error.message.includes('unauthorized')) {
      return 'Authentication failed. Please log in again.';
    }
    
    // Validation errors
    if (error.message.includes('validation') || error.message.includes('invalid')) {
      return 'Please check your input and try again.';
    }
  }
  
  // HTTP status code errors
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status: number }).status;
    
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Please log in to continue.';
      case 403:
        return 'You don\'t have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 422:
        return 'Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      case 504:
        return 'Request timed out. Please try again.';
      default:
        if (status >= 500) {
          return 'Server error. Please try again later.';
        }
    }
  }
  
  // Generic fallback
  return 'Something went wrong. Please try again.';
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validatePhone(phone: string): boolean {
  const phoneRegex = /^[\d\s\-\+\(\)]{8,}$/;
  return phoneRegex.test(phone);
}

export function validateRequired(value: string): boolean {
  return value.trim().length > 0;
}

export function validateABN(abn: string): boolean {
  const abnDigits = abn.replace(/\s/g, '');
  if (abnDigits.length !== 11 || !/^\d+$/.test(abnDigits)) {
    return false;
  }
  
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  
  for (let i = 0; i < 11; i++) {
    const digit = parseInt(abnDigits[i], 10);
    const weight = weights[i];
    sum += (i === 0 ? digit - 1 : digit) * weight;
  }
  
  return sum % 89 === 0;
}

export function validatePostcode(postcode: string): boolean {
  return /^\d{4}$/.test(postcode);
}

export function validateCurrency(value: string): boolean {
  return /^\d+(\.\d{1,2})?$/.test(value);
}